/**
 * Llamadas a la API del catálogo de proyectos.
 * Ver `apps/api/src/modules/proyectos/README.md` para los permisos y los errores 409.
 */
import {
  proyectoSchema,
  type ActualizarProyectoEntrada,
  type CrearProyectoEntrada,
} from '@cdtec-backlog/contrato';
import { z } from 'zod';
import { ErrorDeApi, solicitar } from '../../lib/api';

export const listarProyectos = () => solicitar('/proyectos', { esquema: z.array(proyectoSchema) });

/** Cualquiera con permiso de escritura. 409 `proyecto_duplicado` si el nombre ya existe. */
export const crearProyecto = (entrada: CrearProyectoEntrada) =>
  solicitar('/proyectos', { metodo: 'POST', cuerpo: entrada, esquema: proyectoSchema });

/** Solo ADMIN (la API lo exige): nombre, activo y enlace al repo. */
export const actualizarProyecto = (id: number, cambios: ActualizarProyectoEntrada) =>
  solicitar(`/proyectos/${id}`, {
    metodo: 'PATCH',
    cuerpo: cambios,
    esquema: proyectoSchema,
  });

/** Solo ADMIN. 409 `proyecto_en_uso` si alguna tarea lo usa. */
export const eliminarProyecto = (id: number) => solicitar(`/proyectos/${id}`, { metodo: 'DELETE' });

/**
 * Si el error es `proyecto_duplicado`, devuelve el proyecto que ya existía. La API entrega sus
 * campos como texto dentro de `detalles` (`id`, `nombre`, `activo`).
 */
export function proyectoExistente(error: unknown) {
  if (!(error instanceof ErrorDeApi) || error.codigo !== 'proyecto_duplicado') return null;
  const valor = (campo: string) => error.detalles.find((d) => d.campo === campo)?.mensaje;
  const id = Number(valor('id'));
  const nombre = valor('nombre');
  if (!Number.isInteger(id) || id <= 0 || !nombre) return null;
  return { id, nombre, activo: valor('activo') === 'true' };
}
